import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Context } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import { tags as tagsApi, tag as tagApi } from 'utilities/api';
import { colorsMatch, mapFromStandardColor } from 'constants/colors';
import { SET_TAG_CHANGES, SET_TAG_CATALOGUE } from 'reducers/reducer';

const useTagsData = () => {
  const [{ organization, tagChanges, tagCatalogue }, dispatch] = useContext(
    Context
  );
  const [tags, setTags] = useState([]);
  const [catalogueScope, setCatalogueScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const requestIdRef = useRef(0);
  const organizationIdRef = useRef(organization?.id);
  organizationIdRef.current = organization?.id;
  const scopedCatalogue =
    tagCatalogue && tagCatalogue.organizationId === organization?.id
      ? tagCatalogue
      : null;
  const hasLocalCatalogue =
    catalogueScope && catalogueScope.organizationId === organization?.id;

  const getTags = useCallback(() => {
    if (organization?.id !== organizationIdRef.current) return () => {};
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const organizationId = organization?.id;
    const isCurrentRequest = () =>
      requestId === requestIdRef.current &&
      organizationId === organizationIdRef.current;
    setLoading(true);
    const { promise, cancel } = tagsApi.fetch(null, { size: -1 });
    promise
      .then(({ data }) => {
        if (!isCurrentRequest()) return;
        // Sort tags alphabetically by name
        const sortedTags = [...data.results].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setTags(sortedTags);
        setCatalogueScope({ organizationId });
        dispatch?.({
          type: SET_TAG_CATALOGUE,
          payload: { organizationId, tags: sortedTags }
        });
      })
      .catch(catchCancel)
      .catch(err => {
        if (!isCurrentRequest()) return;
        parseServerError(dispatch)(err);
      })
      .finally(() => {
        if (isCurrentRequest()) setLoading(false);
      });

    return cancel;
  }, [dispatch, organization?.id]);

  const saveTags = useCallback(
    async (formValues, enhancedTags) => {
      setSaving(true);

      try {
        // Separate create, update, and delete operations
        const createPromises = [];
        const updatePromises = [];
        const deletePromises = [];
        const updatedTags = [];

        // Handle creates and updates
        Object.entries(formValues.tags).forEach(([tagId, tagData]) => {
          if (tagData.source === 'create') {
            // New tag - only create if name is not empty
            if (tagData.name && tagData.name.trim().length > 0) {
              const tagForApi = {
                name: tagData.name.trim(),
                description: '',
                color: mapFromStandardColor(tagData.color)
              };
              const { promise } = tagApi.create(tagForApi);
              createPromises.push(promise);
            }
            // Skip creating tags with empty names
          } else {
            // Existing tag - check if it needs updating
            const originalTag = enhancedTags.find(t => t.id === tagId);
            if (
              originalTag &&
              (originalTag.name !== tagData.name ||
                !colorsMatch(originalTag.color, tagData.color))
            ) {
              // Tag was modified - update
              const tagForApi = {
                name: tagData.name.trim(),
                description: '',
                color: mapFromStandardColor(tagData.color)
              };
              const { promise } = tagApi.update(tagId, tagForApi);
              updatePromises.push(promise);
              updatedTags.push({ id: tagId, ...tagForApi });
            }
          }
        });

        // Handle deletes - find tags that exist in enhancedTags but not in formValues.tags
        const formTagIds = new Set(Object.keys(formValues.tags || {}));
        const deletedTags = enhancedTags.filter(tag => !formTagIds.has(tag.id));

        deletedTags.forEach(tag => {
          const { promise } = tagApi.delete(tag.id);
          deletePromises.push(promise);
        });

        // Execute all operations
        const [
          createResults,
          updateResults,
          deleteResults
        ] = await Promise.all([
          Promise.allSettled(createPromises),
          Promise.allSettled(updatePromises),
          Promise.allSettled(deletePromises)
        ]);
        const savedUpdates = updatedTags.filter(
          (_, index) => updateResults[index].status === 'fulfilled'
        );
        const deletedTagIds = deletedTags
          .filter((_, index) => deleteResults[index].status === 'fulfilled')
          .map(tag => tag.id);
        if (savedUpdates.length || deletedTagIds.length) {
          dispatch({
            type: SET_TAG_CHANGES,
            payload: {
              organizationId: organization?.id,
              updatedTags: savedUpdates,
              deletedTagIds
            }
          });
        }

        // Check for failures
        const failures = [
          ...createResults,
          ...updateResults,
          ...deleteResults
        ].filter(result => result.status === 'rejected');
        const successes = [
          ...createResults,
          ...updateResults,
          ...deleteResults
        ].filter(result => result.status === 'fulfilled');
        const createSuccesses = createResults.filter(
          result => result.status === 'fulfilled'
        );
        const updateSuccesses = updateResults.filter(
          result => result.status === 'fulfilled'
        );
        const deleteSuccesses = deleteResults.filter(
          result => result.status === 'fulfilled'
        );

        // Refresh tags data from API (even if some failed, we want to show what succeeded)
        await getTags();

        const returnValue = {
          createSuccesses,
          updateSuccesses,
          deleteSuccesses,
          success: false,
          partialSuccess: false,
          error: null
        };

        if (failures.length === 0) {
          returnValue.success = true;
          // All succeeded
          return returnValue;
        }
        if (successes.length > 0) {
          // Partial success - some saved, some failed
          returnValue.partialSuccess = true;
          returnValue.error = new Error('Some tags failed to save');
          return returnValue;
        }
        // Total failure - none saved
        returnValue.error = new Error('All tags failed to save');
        return returnValue;
      } catch (error) {
        // Handle error with proper error handling
        parseServerError(dispatch)(error);
        return {
          success: false,
          partialSuccess: false,
          createSuccesses: [],
          updateSuccesses: [],
          deleteSuccesses: [],
          error
        };
      } finally {
        setSaving(false);
      }
    },
    [dispatch, getTags, organization?.id]
  );

  useEffect(() => {
    if (!tagChanges || tagChanges.organizationId !== organization?.id) return;
    const updatedById = new Map(
      tagChanges.updatedTags.map(tag => [tag.id, tag])
    );
    setTags(previous =>
      previous
        .filter(tag => !tagChanges.deletedTagIds.includes(tag.id))
        .map(tag =>
          updatedById.has(tag.id) ? { ...tag, ...updatedById.get(tag.id) } : tag
        )
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [tagChanges, organization?.id]);

  useEffect(() => {
    const cancel = getTags();

    return () => {
      cancel();
    };
  }, [getTags]);

  return {
    tags: scopedCatalogue?.tags ?? (hasLocalCatalogue ? tags : []),
    catalogueLoaded: !!(scopedCatalogue || hasLocalCatalogue),
    loading,
    saving,
    getTags,
    saveTags
  };
};

export default useTagsData;
